"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Horizontal scroller with a visible progress bar and arrow buttons, so a
 * visitor can tell there is more to the right. Children set their own width.
 */
export function ScrollRow({ children, className, bleed = false }: { children: ReactNode; className?: string; bleed?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState({ start: 0, width: 1, canScroll: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 4) {
        setProgress({ start: 0, width: 1, canScroll: false });
        return;
      }
      setProgress({
        start: el.scrollLeft / el.scrollWidth,
        width: el.clientWidth / el.scrollWidth,
        canScroll: true,
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  const nudge = (dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <div className={cn("relative", className)}>
      <div
        ref={ref}
        className={cn(
          "flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          bleed && "-mx-4 px-4 md:-mx-8 md:px-8"
        )}
      >
        {children}
      </div>
      {progress.canScroll && (
        <div className="mt-3 flex items-center gap-4">
          <div className="relative h-1 flex-1 overflow-hidden rounded-full bg-line" aria-hidden="true">
            <div
              className="absolute top-0 h-full rounded-full bg-ink transition-[left,width] duration-150"
              style={{ left: `${progress.start * 100}%`, width: `${Math.max(progress.width * 100, 8)}%` }}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => nudge(-1)}
              aria-label="Scroll left"
              className="flex size-9 items-center justify-center rounded-full border border-line bg-white text-ink hover:bg-surface"
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
            </button>
            <button
              type="button"
              onClick={() => nudge(1)}
              aria-label="Scroll right"
              className="flex size-9 items-center justify-center rounded-full border border-line bg-white text-ink hover:bg-surface"
            >
              <ChevronRight aria-hidden="true" className="size-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
